import React from "react";
import { TextField, Box, Typography, Divider } from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CustomerDetails } from "../../../types/salon.types";
import { useCustomerSelection } from "../../../hooks/useCustomerSelection";
import LoadingScreen from "src/shared/components/LoadingScreen";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import CommonButtons from "src/shared/components/CommonButtons";
import CommonTable, { HeadCell } from "src/business-types/grocery/components/CommonTable";
import { useUser } from "src/shared/context/UserContext";

interface CustomerSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onCustomerSelect: (customer: CustomerDetails) => void;
  onAddNewCustomer: () => void;
}

interface TableRow {
  id: string | number;
  sno: number;
  Name: string;
  phone_number: string;
}

const CustomerSelectionDialog: React.FC<CustomerSelectionDialogProps> = ({ open, onClose, onCustomerSelect, onAddNewCustomer }) => {
  const { customers, searchTerm, setSearchTerm, loading, pagination, handleNextPage, handlePrevPage } = useCustomerSelection(open);

  const { user } = useUser();
  const organizationName = user?.orgName || "Organization";

  if (!open) return null;

  // 🔢 Define columns for CommonTable
  const headCells: HeadCell<TableRow>[] = [
    { id: "sno", label: "S.No" },
    { id: "Name", label: "Name" },
    { id: "phone_number", label: "Phone Number" },
  ];

  // 🧠 Map customers to table-friendly format (no useMemo)
  const tableData: TableRow[] = customers.map((c, index) => ({
    id: c._id?.$oid || index,
    sno: (pagination.currentPage - 1) * pagination.pageSize + index + 1,
    Name: c.firstName || "-",
    phone_number: c.phones[0].phone || "-",
  }));

  return (
    <ModalWrapper title="Select Customer" onClose={onClose} showBackButton onBack={onClose} showCloseButton width="max-w-xl">
      <div className="flex flex-col gap-1 -mt-3">
        <div className="flex items-center justify-between gap-3">
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by name or phone"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: "60%", fontFamily: "var(--fontfamily)" }}
          />
          <CommonButtons className="w-40" label="Add Customer" onClick={onAddNewCustomer} />
        </div>

        {/* 📋 Table Content */}
        {loading ? (
          <LoadingScreen />
        ) : (
          <div className="flex-1 overflow-y-auto w-full mt-2">
            <CommonTable
              className="w-full min-w-[515px] max-w-[900px] h-[422px]"
              data={tableData}
              headCells={headCells}
              title="Customer List"
              selectable={false} // no checkboxes
              onRowClick={(id) => {
                const selected = customers.find((c, index) => c._id?.$oid === id || index === Number(id));
                if (selected) onCustomerSelect(selected);
              }}
              showToolbar={false}
              showFilterIcon={false}
              pagenation={false}
              pageSize={pagination.pageSize}
            />
          </div>
        )}

        
        <Box display="flex" flexDirection="column" gap={2} className="mt-2">
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <CommonButtons className="w-32" label="Previous" icon={ChevronLeft} onClick={handlePrevPage} disabled={pagination.currentPage === 1} />

            <Box display="flex" alignItems="center" gap={2} color="text.secondary" fontSize="0.875rem" sx={{ fontFamily: "var(--fontfamily)" }}>
              <Typography variant="body2">
                Page <strong>{pagination.currentPage}</strong> of <strong>{pagination.totalPages}</strong>
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ height: 28 }} />
              <Typography variant="body2">
                Total Customers: <strong>{pagination.totalRecords}</strong>
              </Typography>
            </Box>

            <CommonButtons className="w-24" label="Next" icon={ChevronRight} iconPosition="right" onClick={handleNextPage} disabled={pagination.currentPage === pagination.totalPages} />
          </Box>

          {/* Close Button */}
          {/* <Box display="flex" justifyContent="flex-end" width="100%">
            <CommonButtons className="w-32" label="Close" variant="outlineDanger" onClick={onClose} />
          </Box> */}
        </Box>
      </div>
    </ModalWrapper>
  );
};

export default CustomerSelectionDialog;
